<template>
  <div>
    <div
      class="relative group text-app-color flex items-center justify-between
             cursor-pointer transition duration-300 select-none text-15 pr-20"
      :class="[
        { 'mr-10': isPoseidonTheme && !isPaperShape}
      ]"
      @dblclick="toggleTitleEditMode"
    >
      <div
        class="flex items-center w-full py-5"
        :class="projectItemContentClasses"
        @click="setProject"
      >
        <Icons ref="iconsRef" :project="localProject" class="pr-10 flex-shrink-0" @setIcon="setIcon" />

        <p
          v-if="!isEdit"
          class="truncate text-14"
          :class="{ 'font-bold': isActive }"
        >
          {{ localProject.title }}
        </p>
        <div v-else>
          <input
            ref="titleInput"
            v-model="localProject.title"
            class="w-full text-14 rounded outline-none"
            :class="[isDarkMode ? 'bg-canvas' : 'bg-blue-lighter', { 'font-bold': isActive }]"
            type="text"
            @keypress.enter="edit"
            @blur="edit"
            @keypress="save"
            @click.stop
          >
        </div>
      </div>
      <div class="h-full absolute top-0 right-20 flex items-center pl-5 transition-color duration-300" :class="hoverBg">
        <transition name="fade">
          <div
            class="anim-opacity flex items-center"
            :class="showMenu || (isMobile && isActive) ? 'opacity-100' : 'opacity-0 md:group-hover:opacity-100'"
          >
            <div v-if="level !== 3">
              <ButtonModule
                prefix="icon-add text-12 text-app-color"
                has-hover
                @click="addNewProject(project.id)"
              />
            </div>

            <DropdownModule
              icon="icon-more-horizontal text-20 opacity-50 hover:opacity-100 transition-color duration-300"
              classes="hover:bg-project-hover px-5 py-3 rounded-6 anim-color-200"
              :icon-has-hover="false"
              class="flex text-app-color"
              :width="200"
              background="bg-canvas"
              remove-optional-top-gap
              :content-height="159"
              @click="showMenu = !showMenu"
              @close="showMenu = false"
            >
              <div class="text-15">
                <div class="hover:bg-selection rounded-t-10">
                  <ButtonModule
                    ref="button"
                    text="Rename"
                    :prefix="`icon-edit mr-15 ${isDraculaTheme ? 'text-main-color' : 'text-app-color'}`"
                    class="text-app-color p-12 w-full"
                    @click="toggleTitleEditMode"
                  />
                </div>
                <MoveTo v-if="showMenu && !isHeadquarters" :project="localProject" :book="book" />
                <div class="hover:bg-selection rounded-b-10">
                  <ButtonModule
                    text="Delete"
                    :prefix="`icon-delete ml-1 mr-15 text-16 ${isDraculaTheme ? 'text-main-color' : 'text-app-color'}`"
                    class="text-app-color p-12 w-full"
                    @click="$emit('delete', localProject)"
                  />
                </div>
              </div>
            </DropdownModule>
          </div>
        </transition>
        <transition name="fade">
          <ButtonModule
            v-if="localProject.subProject?.length"
            :has-hover="true"
            :prefix="folded ?
              'icon-keyboard-arrow-left opacity-50 group-hover:opacity-100' :
              'icon-keyboard-arrow-down opacity-100'
            "
            class="text-app-color text-12"
            @click.stop="toggleSubProjects"
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import Icons from '@/components/projects/Icons'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import MoveTo from '@/components/shared/MoveTo'

import localStorageService from '@/core/local-storage'

import { defineComponent, ref, computed, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default defineComponent({
  name: 'ProjectItem',

  components: {
    ButtonModule,
    Icons,
    DropdownModule,
    MoveTo
  },

  props: {
    project: { type: Object },
    book: { type: Object },
    folded: Boolean,
    level: Number,
    isProjectInstruction: { type: Boolean },
    isProjectInstructionsAccepted: { type: Boolean }
  },

  emits: ['delete', 'fold', 'rename'],

  setup (props, { emit }) {
    const { state, getters, commit, dispatch } = useStore()
    const router = useRouter()
    const route = useRoute()

    let temporaryTitle = ''

    const iconsRef = ref(null)

    const isEdit = ref(false)
    const showMenu = ref(false)
    const titleInput = ref(null)

    const localProject = computed(() => props.project)
    const isCurrentProject = computed(() => state?.project?.currentProject?.id === props?.project?.id)

    const files = computed(() => state.file.files)
    const isDraculaTheme = computed(() => state.settings.settings.theme === 'Dracula')
    const isPaperShape = computed(() => getters.isPaperShape)
    const isPoseidonTheme = computed(() => state.settings.settings.theme === 'Poseidon')

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)

    const isHeadquarters = computed(() => route.name === 'Headquarters')

    const isActive = computed(() => {
      return +route.query?.page === localProject.value.id
    })

    const hoverBg = computed(() => {
      if (isMobile.value) {
        return isActive.value ? 'bg-project' : ''
      } else if (showMenu.value && !props.isPoseidonTheme) {
        return isActive.value ? 'bg-project' : 'bg-book group-hover:bg-project-hover'
      } else {
        return isActive.value ? 'group-hover:bg-project' : 'group-hover:bg-project-hover'
      }
    })

    const projectItemContentClasses = computed(() => {
      if (isPaperShape.value) {
        if (!localProject.value.parentId) {
          return localProject.value.subProject.length ? 'max-w-190 pl-20' : 'max-w-200 pl-20'
        } else {
          return localProject.value.subProject.length ? 'pl-20 max-w-170' : 'pl-20 max-w-200'
        }
      }

      if (isPoseidonTheme.value) {
        // if project doesn't have a sub projects
        if (!localProject.value.subProject) {
          return 'max-w-210 pl-60'
        }

        if (!localProject.value.parentId) {
          return localProject.value.subProject.length ? 'max-w-210 pl-20' : 'max-w-225 pl-20'
        } else {
          return localProject.value.subProject.length ? 'pl-20 max-w-210' : 'pl-20 max-w-225'
        }
      } else {
        // if project doesn't have a sub projects
        if (!localProject.value.subProject) {
          return 'max-w-185 pl-20 md:pl-40'
        }

        return localProject.value.parentId ? 'max-w-205 pl-20 md:pl-35' : 'max-w-225 pl-25 md:pl-30'
      }
    })

    function toggleSubProjects () {
      emit('fold')
      const subProjects = localStorageService.getItem('default-nested-projects') || []

      if (!props.folded) {
        subProjects.push(props.project.id)
        localStorageService.setItem('default-nested-projects', subProjects)
      } else {
        localStorageService.setItem('default-nested-projects', subProjects.filter(p => p !== props.project.id))
      }
    }

    function setProject (event) {
      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === props.book.id)) {
        return commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
      }

      if (iconsRef.value.$el.contains(event.target)) return

      if (isMobile.value) commit('updateSidebar', false)

      router.replace({ query: { page: localProject.value.id } })
    }

    function addNewProject () {
      dispatch('addProject', localProject.value.id)
    }

    function edit () {
      isEdit.value = false

      if (localProject.value.title.trim() === '') {
        localProject.value.title = temporaryTitle
      } else if (temporaryTitle !== localProject.value.title) {
        emit('rename', {
          id: localProject.value.id,
          title: localProject.value.title
        })
        commit('loading', true)
        dispatch('editProject', {
          id: localProject.value.id,
          data: {
            title: localProject.value.title
          }
        })
        if (isCurrentProject.value) {
          commit('setCurrentProject', localProject.value)
        }
      }
    }

    let timer = null
    function save () {
      clearTimeout(timer)
      timer = setTimeout(() => {
        commit('setIconAndTitle', {
          projectId: localProject.value.id,
          title: localProject.value.title,
          icon: localProject.value.icon
        })
      }, 100)
    }

    function toggleTitleEditMode () {
      temporaryTitle = localProject.value.title
      isEdit.value = true

      nextTick(_ => {
        titleInput.value.focus()
        titleInput.value.select()
      })
    }

    function setIcon (icon) {
      localProject.value.icon = icon

      commit('setIconAndTitle', {
        projectId: localProject.value.id,
        title: localProject.value.title,
        icon: icon
      })

      return dispatch('editProject', {
        id: localProject.value.id,
        data: {
          title: localProject.value.title,
          icon: icon
        }
      })
    }

    function onDelete (element) {
      emit('delete', element)
    }

    onMounted(() => {
      window.onbeforeunload = () => {
        Object.entries(files.value).forEach(([id, item]) => {
          if (item.percent >= 0) {
            commit('deleteFile', id)
          }
        })
      }
    })

    return {
      // data
      isEdit,
      showMenu,
      localProject,
      // refs
      titleInput,
      iconsRef,
      // computed
      isDarkMode,
      isMobile,
      isActive,
      hoverBg,
      projectItemContentClasses,
      isPaperShape,
      isPoseidonTheme,
      isDraculaTheme,
      isHeadquarters,
      // function
      setProject,
      addNewProject,
      edit,
      save,
      toggleTitleEditMode,
      setIcon,
      onDelete,
      toggleSubProjects
    }
  }
})
</script>
